import React, { useState } from 'react';
import { Badge, BmModal, Btn, EmptyState, Field, Input, Select } from '../ForBrands/ui/BrandUI';
import { Icon } from '../ForBrands/ui/icons';

// Admin Mode > Codes > Customize options: edits the free form `options` object
// stored on one code. Each field holds a string, a number, an array or an
// object, and arrays and objects nest. The editor opens in a wide BmModal and
// hands the whole object to onSave; the caller sends it.

const TYPES = ['string', 'number', 'array', 'object'];

const getDefaultValueForType = (type) => {
  switch (type) {
    case 'number': return 0;
    case 'string': return '';
    case 'array': return [];
    case 'object': return {};
    default: return null;
  }
};

const typeOf = (value) => {
  if (value === null) return 'null';
  return Array.isArray(value) ? 'array' : typeof value;
};

const TypeOptions = () => TYPES.map((t) => <option key={t} value={t}>{t}</option>);

const RemoveButton = ({ label, onClick }) => (
  <button
    type="button"
    aria-label={label}
    title={label}
    onClick={onClick}
    className="mt-1 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-bm-muted transition-colors hover:bg-bm-subtle hover:text-bm-critical"
  >
    <Icon name="trash" />
  </button>
);

// Adds an item of the chosen type to an array.
const TypeSelector = ({ label, onSelect }) => {
  const [selectedType, setSelectedType] = useState('string');
  return (
    <div className="flex flex-wrap items-center gap-2">
      <Select
        aria-label={`Type of the new item in ${label}`}
        value={selectedType}
        onChange={(e) => setSelectedType(e.target.value)}
        className="!h-8 !w-auto"
      >
        <TypeOptions />
      </Select>
      <Btn variant="secondary" size="sm" onClick={() => onSelect(selectedType)}>
        <Icon name="plus" size={14} className="mr-1" />
        Add item
      </Btn>
    </div>
  );
};

// Adds a key of the chosen type to an object (it was two browser prompts).
const KeyAdder = ({ label, existing, onAdd }) => {
  const [key, setKey] = useState('');
  const [type, setType] = useState('string');
  const taken = !!key && Object.prototype.hasOwnProperty.call(existing, key);
  const add = () => {
    if (!key || taken) return;
    onAdd(key, type);
    setKey('');
  };
  return (
    <div className="flex flex-col gap-1">
      <div className="flex flex-wrap items-center gap-2">
        <Input
          aria-label={`New key in ${label}`}
          placeholder="New key"
          value={key}
          onChange={(e) => setKey(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              add();
            }
          }}
          className="!h-8 !w-[180px] max-w-full"
        />
        <Select
          aria-label={`Type of the new key in ${label}`}
          value={type}
          onChange={(e) => setType(e.target.value)}
          className="!h-8 !w-auto"
        >
          <TypeOptions />
        </Select>
        <Btn variant="secondary" size="sm" onClick={add} disabled={!key || taken}>
          <Icon name="plus" size={14} className="mr-1" />
          Add key
        </Btn>
      </div>
      {taken && <span className="bm-body-s text-bm-critical">{label} already has a key named {key}.</span>}
    </div>
  );
};

const renderValueEditor = (value, type, onChange, label) => {
  if (type === 'number') {
    return (
      <Input
        type="number"
        aria-label={label}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
      />
    );
  }
  if (type === 'string') {
    return <Input type="text" aria-label={label} value={value} onChange={(e) => onChange(e.target.value)} />;
  }
  if (type === 'array') {
    return (
      <div className="flex w-full min-w-0 flex-col gap-2">
        {value.map((item, i) => (
          <div key={i} className="flex items-start gap-2 border-l-2 border-bm-border pl-3">
            <span className="mt-[11px] w-6 shrink-0 bm-label-xs text-bm-tertiary">{i + 1}</span>
            <div className="min-w-0 flex-1">
              <RecursiveEditor
                value={item}
                label={`${label} item ${i + 1}`}
                onChange={(newItem) => {
                  const updated = [...value];
                  updated[i] = newItem;
                  onChange(updated);
                }}
              />
            </div>
            <RemoveButton label={`Remove ${label} item ${i + 1}`} onClick={() => onChange(value.filter((_, idx) => idx !== i))} />
          </div>
        ))}
        <TypeSelector label={label} onSelect={(t) => onChange([...value, getDefaultValueForType(t)])} />
      </div>
    );
  }
  if (type === 'object') {
    return (
      <div className="flex w-full min-w-0 flex-col gap-2">
        {Object.entries(value).map(([k, v]) => (
          <div key={k} className="flex flex-wrap items-start gap-2 border-l-2 border-bm-border pl-3 sm:flex-nowrap">
            <span className="mt-[10px] w-full shrink-0 truncate bm-label-l text-bm-ink sm:w-[140px]" title={k}>{k}</span>
            <div className="min-w-0 flex-1">
              <RecursiveEditor
                value={v}
                label={`${label} ${k}`}
                onChange={(newVal) => {
                  onChange({ ...value, [k]: newVal });
                }}
              />
            </div>
            <RemoveButton
              label={`Remove ${label} ${k}`}
              onClick={() => {
                const { [k]: _, ...rest } = value;
                onChange(rest);
              }}
            />
          </div>
        ))}
        <KeyAdder label={label} existing={value} onAdd={(key, t) => onChange({ ...value, [key]: getDefaultValueForType(t) })} />
      </div>
    );
  }
  // null, true and false have no editor of their own; show them as stored.
  return <span className="inline-flex h-10 items-center bm-body-m text-bm-muted">{String(value)}</span>;
};

const RecursiveEditor = ({ value, onChange, label }) => renderValueEditor(value, typeOf(value), onChange, label);

const OptionEditorModal = ({ code, onClose, onSave }) => {
  const [fields, setFields] = useState(() => (
    code.options && typeof code.options === 'object' && !Array.isArray(code.options) ? code.options : {}
  ));
  const [newFieldName, setNewFieldName] = useState('');
  const [newFieldType, setNewFieldType] = useState('string');

  const taken = !!newFieldName.trim() && Object.prototype.hasOwnProperty.call(fields, newFieldName);
  const addField = () => {
    if (!newFieldName.trim() || taken) return;
    setFields(prev => ({ ...prev, [newFieldName]: getDefaultValueForType(newFieldType) }));
    setNewFieldName('');
  };

  const entries = Object.entries(fields);

  return (
    <BmModal
      title="Customize options"
      width={720}
      onClose={onClose}
      footer={
        <>
          <Btn variant="secondary" className="ml-auto" onClick={onClose}>Cancel</Btn>
          <Btn onClick={() => onSave(fields)} disabled={entries.length === 0}>Save options</Btn>
        </>
      }
    >
      <p className="bm-body-m text-bm-muted">
        The fields stored on <span className="bm-label-l text-bm-ink">{code.name}</span>. A value is a string, a number, an array or an object, and arrays and objects can nest.
      </p>

      <form
        className="mt-5 flex flex-wrap items-end gap-3 rounded-[8px] border border-bm-border bg-bm-subtle p-4"
        onSubmit={(e) => {
          e.preventDefault();
          addField();
        }}
      >
        <Field label="New field" className="min-w-[180px] flex-1">
          <Input placeholder="Field name" value={newFieldName} onChange={(e) => setNewFieldName(e.target.value)} />
        </Field>
        <Field label="Type" className="w-[140px]">
          <Select value={newFieldType} onChange={(e) => setNewFieldType(e.target.value)}>
            <TypeOptions />
          </Select>
        </Field>
        <Btn type="submit" disabled={!newFieldName.trim() || taken}>
          <Icon name="plus" className="mr-1.5" />
          Add field
        </Btn>
        {taken && <span className="w-full bm-body-s text-bm-critical">This code already has a field named {newFieldName}.</span>}
      </form>

      <div className="mt-5 flex flex-col gap-3">
        {entries.length === 0 ? (
          <EmptyState>No fields yet. Add the first one above, then save.</EmptyState>
        ) : (
          entries.map(([field, value]) => (
            <section key={field} aria-label={field} className="rounded-[8px] border border-bm-border p-4">
              <div className="mb-3 flex items-center gap-2">
                <h3 className="min-w-0 truncate bm-h-xs text-bm-ink">{field}</h3>
                <Badge tone="soft">{typeOf(value)}</Badge>
              </div>
              <RecursiveEditor
                value={value}
                label={field}
                onChange={(newVal) => setFields(prev => ({ ...prev, [field]: newVal }))}
              />
            </section>
          ))
        )}
      </div>
    </BmModal>
  );
};

// `visible` is the code being edited (null when closed). Each open starts
// from that code's saved options, so edits never carry over between codes.
const AdvancedOptionEditor = ({ visible, onClose, onSave }) => {
  if (!visible) return null;
  return <OptionEditorModal key={visible._id} code={visible} onClose={onClose} onSave={onSave} />;
};

export default AdvancedOptionEditor;
